import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useBackGuard } from '@/hooks/use-back-guard';
import { Button } from '@/components/ui/button';
import { UserRound } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { giveAskHeading, type GiveAskItem } from '@/lib/give-ask-api';
import type { ConclaveTableMate } from './ConclaveTableMateCard';

interface MateGiveAskSheetProps {
  mate: ConclaveTableMate | null;
  kind: 'give' | 'ask';
  onOpenChange: (open: boolean) => void;
  onReply: (item: GiveAskItem) => void;
}

export function MateGiveAskSheet({ mate, kind, onOpenChange, onReply }: MateGiveAskSheetProps) {
  useBackGuard(mate !== null, () => onOpenChange(false));
  const items = mate?.giveAskItems ? (kind === 'give' ? mate.giveAskItems.gives : mate.giveAskItems.asks) : null;
  const heading = kind === 'give' ? 'gives' : 'asks';

  return (
    <Sheet open={mate !== null} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader>
          <div className="flex items-center gap-3">
            {mate ? (
              <Avatar className="h-10 w-10 shrink-0 rounded-full border-2 border-primary/40 ring-2 ring-primary/15">
                <AvatarImage src={mate.image ?? undefined} alt={mate.name} />
                <AvatarFallback>
                  <UserRound className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
            ) : null}
            <SheetTitle>{mate ? `${mate.name}'s ${heading}` : heading}</SheetTitle>
          </div>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {items === null ? (
            <p className="text-sm text-muted-foreground">Their {heading} are not visible in this event.</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No {heading} enabled for this event yet.</p>
          ) : (
            items.map((item) => (
              <div key={item.id} className="rounded-xl border bg-white p-4">
                <p className="font-medium">{giveAskHeading(item)}</p>
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{item.description}</p>
                {item.category ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {[item.category, item.subCategory].filter(Boolean).join(' > ')}
                  </p>
                ) : null}
                {kind === 'ask' ? (
                  <Button size="sm" className="mt-3" onClick={() => onReply(item)}>
                    Reply with a lead
                  </Button>
                ) : null}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
